import React, { useMemo, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Card, CardBody, StatusPill, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import EditRecordModal, { FieldDef } from '../../modals/EditRecordModal';
import ConfirmDialog from '../../modals/ConfirmDialog';
import { Team } from '../../PanelComerciales.types';
import { isCurrency, isNonEmptyTeamName } from '../../utils/validators';

interface TeamsTableProps {
    teams: Team[];
    onTeamUpsert: (next: Team, prevName?: string) => void;
    onTeamDeactivate: (name: string) => void;
}

type TeamDraft = Team & Record<string, unknown>;

const TeamsTable: React.FC<TeamsTableProps> = ({ teams, onTeamUpsert, onTeamDeactivate }) => {
    const [editing, setEditing] = useState<Team | null>(null);
    const [confirming, setConfirming] = useState<Team | null>(null);

    const fields: FieldDef<TeamDraft>[] = useMemo(
        () => [
            {
                key: 'name',
                label: 'Nombre',
                kind: 'text',
                validator: (v) =>
                    isNonEmptyTeamName(v as string) ? null : 'Nombre requerido (máx 60)',
            },
            {
                key: 'leader',
                label: 'Líder',
                kind: 'text',
                validator: (v) => (isNonEmptyTeamName(v as string) ? null : 'Líder requerido'),
            },
            {
                key: 'goal',
                label: 'Meta',
                kind: 'text',
                validator: (v) =>
                    isCurrency(v as string) ? null : 'Meta inválida (ej: $1,400,000)',
            },
            { key: 'active', label: 'Activo', kind: 'switch', helper: 'Desactiva el equipo si está en pausa' },
        ],
        [],
    );

    const handleSave = (next: TeamDraft) => {
        const team: Team = {
            name: next.name,
            leader: next.leader,
            goal: next.goal,
            active: next.active,
            members: editing?.members ?? 0,
        };
        onTeamUpsert(team, editing?.name);
        setEditing(null);
    };

    return (
        <Card>
            <SectionHeader
                title='Equipos'
                icon={<GroupsOutlinedIcon style={{ color: '#7C3AED' }} />}
            />
            <CardBody>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th>Equipo</th>
                                <th>Líder</th>
                                <th>Miembros</th>
                                <th>Meta</th>
                                <th>Estado</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teams.map((t) => (
                                <tr key={t.name}>
                                    <td><strong>{t.name}</strong></td>
                                    <td>{t.leader}</td>
                                    <td>{t.members}</td>
                                    <td>{t.goal}</td>
                                    <td>
                                        <StatusPill $variant={t.active ? 'success' : 'muted'}>
                                            {t.active ? 'Activo' : 'Inactivo'}
                                        </StatusPill>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Editar'>
                                            <IconButton size='small' onClick={() => setEditing(t)}>
                                                <EditOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        {t.active && (
                                            <Tooltip title='Desactivar'>
                                                <IconButton size='small' onClick={() => setConfirming(t)}>
                                                    <BlockOutlinedIcon fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {teams.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                                        No hay equipos configurados
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            {editing && (
                <EditRecordModal<TeamDraft>
                    open={Boolean(editing)}
                    title={`Editar equipo · ${editing.name}`}
                    fields={fields}
                    initial={editing as TeamDraft}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                />
            )}

            <ConfirmDialog
                open={Boolean(confirming)}
                title={`Desactivar equipo ${confirming?.name ?? ''}`}
                description='El equipo dejará de aparecer como opción en los selectores. Puedes reactivarlo cuando quieras.'
                danger
                confirmLabel='Desactivar'
                onCancel={() => setConfirming(null)}
                onConfirm={() => {
                    if (confirming) onTeamDeactivate(confirming.name);
                    setConfirming(null);
                }}
            />
        </Card>
    );
};

export default TeamsTable;
